/**
 * Generates a simple hash for the device based on user agent and screen properties
 * @returns {string} A hash string representing the device
 */
function generateDeviceHash() {
  const userAgent = navigator.userAgent;
  const screenInfo = `${screen.width}x${screen.height}x${screen.colorDepth}`;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // Simple hash function (for demo purposes - in production you might want a more robust solution)
  const hashSource = `${userAgent}${screenInfo}${timezone}`;
  let hash = 0;
  for (let i = 0; i < hashSource.length; i++) {
    const char = hashSource.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(16);
}

/**
 * Simple device detection using navigator properties (lightweight alternative)
 * @returns {Object} Basic device information
 */
function getBasicDeviceInfo() {
  const userAgent = navigator.userAgent;
  let deviceType = 'desktop';
  let osName = 'Unknown OS';
  let browserName = 'Unknown Browser';
  
  // Basic device type detection
  if (/Mobile|Android|iPhone|iPad/.test(userAgent)) {
    deviceType = /iPad/.test(userAgent) ? 'tablet' : 'smartphone';
  }
  
  // Basic OS detection
  if (/Windows/.test(userAgent)) osName = 'Windows';
  else if (/Mac/.test(userAgent)) osName = 'macOS';
  else if (/Linux/.test(userAgent)) osName = 'Linux';
  else if (/Android/.test(userAgent)) osName = 'Android';
  else if (/iPhone|iPad/.test(userAgent)) osName = 'iOS';
  
  // Basic browser detection
  if (/Chrome/.test(userAgent)) browserName = 'Chrome';
  else if (/Firefox/.test(userAgent)) browserName = 'Firefox';
  else if (/Safari/.test(userAgent)) browserName = 'Safari';
  else if (/Edge/.test(userAgent)) browserName = 'Edge';
  
  return {
    device: { brand: 'Unknown', type: deviceType },
    os: { name: osName, version: 'Unknown' },
    client: { name: browserName, type: 'browser' }
  };
}

/**
 * Lazy load device detector for detailed detection when needed
 * @returns {Promise<Object>} Device detection result
 */
async function getDetailedDeviceInfo() {
  try {
    // Dynamically import device-detector-js only when needed
    const { DeviceDetector } = await import('device-detector-js');
    const deviceDetector = new DeviceDetector();
    return deviceDetector.parse(navigator.userAgent);
  } catch (error) {
    console.warn('Failed to load detailed device detection, using basic detection:', error);
    return getBasicDeviceInfo();
  }
}

/**
 * Extracts device information including device hash and readable info
 * @param {boolean} useDetailedDetection - Whether to use detailed detection (lazy loads library)
 * @returns {Promise<Object>} Device object with id and info properties
 */
export async function getDeviceInfo(useDetailedDetection = false) {
  try {
    let device;
    
    if (useDetailedDetection) {
      device = await getDetailedDeviceInfo();
    } else {
      device = getBasicDeviceInfo();
    }
    
    const deviceHash = generateDeviceHash();
    
    // Build device info string
    const brand = device.device?.brand || 'Unknown';
    const type = device.device?.type || 'desktop';
    const osName = device.os?.name || 'Unknown OS';
    const osVersion = device.os?.version || 'Unknown';
    const clientName = device.client?.name || 'Unknown Browser';
    const clientType = device.client?.type || 'browser';
    
    const deviceInfo = `${brand} ${type} (${osName}-${osVersion}) - ${clientName} ${clientType}`;
    
    return {
      id: deviceHash,
      info: deviceInfo
    };
  } catch (error) {
    console.error('Error getting device info:', error);
    // Fallback device info
    return {
      id: generateDeviceHash(),
      info: `Unknown device (${navigator.platform}) - ${navigator.userAgent.split(' ')[0]} browser`
    };
  }
}
